import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, Handshake, Heart, ShieldCheck } from "lucide-react";
import { PageHero, CTABand } from "@/components/PageHero";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Stat } from "@/components/Stat";
import { EditorialMarquee } from "@/components/Marquee";

const img = (id, w = 1200) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

const values = [
  {
    icon: Heart,
    title: "Empowerment First",
    copy: "We exist to connect communities, strengthen SMMEs and drive sustainable township growth.",
  },
  {
    icon: ShieldCheck,
    title: "Trust Through Transparency",
    copy: "Digital records and financial transparency on every transaction — building creditworthiness that opens doors to funding.",
  },
  {
    icon: Handshake,
    title: "Partnership Over Vendor",
    copy: "From on-site implementation to ongoing support, we walk the journey with every business we serve.",
  },
  {
    icon: Compass,
    title: "Meet You Where You Are",
    copy: "Purchase, rental or subscription — flexible models for businesses starting small or scaling up.",
  },
];

const chapters = [
  {
    number: "01",
    title: "Rooted in Johannesburg",
    copy: "Dynamic App Solutions is a 100% black-owned payments and software company, headquartered in Johannesburg and operating across Gauteng — with Botswana as an additional market.",
  },
  {
    number: "02",
    title: "Redefining township business",
    copy: "We deliver holistic digital solutions that connect communities and strengthen SMMEs — giving township retailers the tools to compete in a digital economy.",
  },
  {
    number: "03",
    title: "Service-and-solutions model",
    copy: "Platform fees, rentals, licensing and support agreements keep our pricing flexible — so world-class payment technology stays affordable for the businesses that need it most.",
  },
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Us"
        lines={["Technology with a", "township heartbeat"]}
        copy="Dynamic App Solutions is redefining township business empowerment — delivering holistic digital solutions that connect communities, strengthen SMMEs, and drive sustainable growth."
        testid="about-hero"
      />

      <section ref={imgRef} className="mx-auto max-w-7xl px-6 pb-28 md:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <motion.div style={{ y: y1 }} className="lg:col-span-7">
            <Reveal>
              <div className="overflow-hidden rounded-[2.5rem] border border-neutral-200">
                <img
                  src="/assets/about-barber.jpg"
                  alt="Barber giving a haircut in a local township barbershop"
                  data-testid="about-image-owners"
                  className="aspect-[16/11] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </Reveal>
          </motion.div>
          <motion.div style={{ y: y2 }} className="flex flex-col justify-end gap-10 lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-[2.5rem] border border-neutral-200">
                <img
                  src={img("photo-1472851294608-062f824d29cc", 900)}
                  alt="Open sign on a small business shop door"
                  data-testid="about-image-shop"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="border-l-2 border-das-violet pl-6 font-display text-2xl font-medium leading-snug tracking-tight text-das-ink">
                "Every payment builds trust, strengthens creditworthiness, and opens doors to
                funding."
              </p>
            </Reveal>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-neutral-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:grid-cols-2 md:px-12 lg:grid-cols-4">
          <Stat value={100} suffix="%" label="Black-Owned" testid="about-stat-owned" />
          <Stat value={2} suffix="" label="Countries" testid="about-stat-countries" delay={0.1} />
          <Stat value={4} suffix="" label="Industries Served" testid="about-stat-industries" delay={0.2} />
          <Stat value={15} suffix="+" label="Products & Platforms" testid="about-stat-products" delay={0.3} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">Our story</p>
        </Reveal>
        <h2 className="mt-5 font-display text-4xl font-medium tracking-tight text-das-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Three chapters, one mission</MaskedLine>
        </h2>
        <div className="mt-16 space-y-0">
          {chapters.map((c, i) => (
            <Reveal key={c.number} delay={i * 0.08}>
              <div
                data-testid={`about-chapter-${i}`}
                className="grid gap-6 border-t border-neutral-200 py-12 lg:grid-cols-12"
              >
                <span className="font-display text-7xl font-extrabold leading-none text-outline-violet lg:col-span-3">
                  {c.number}
                </span>
                <h3 className="font-display text-3xl font-semibold tracking-tight text-das-ink lg:col-span-4">
                  {c.title}
                </h3>
                <p className="max-w-md text-base leading-relaxed text-neutral-600 lg:col-span-5">
                  {c.copy}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee dark />

      <section className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
            What we stand for
          </p>
        </Reveal>
        <h2 className="mt-5 max-w-xl font-display text-4xl font-medium tracking-tight text-das-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Values that keep us honest</MaskedLine>
        </h2>
        <div className="mt-16 grid gap-6 sm:grid-cols-2">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div
                data-testid={`value-card-${i}`}
                className="group h-full rounded-3xl border border-neutral-200 bg-white p-10 transition-all duration-500 hover:-translate-y-2 hover:border-das-violet/40 hover:shadow-lift"
              >
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-das-violet/10 text-das-deep transition-colors duration-500 group-hover:bg-das-violet group-hover:text-white">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-7 font-display text-2xl font-semibold tracking-tight text-das-ink">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{v.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-20 flex flex-col items-start justify-between gap-8 rounded-[2rem] border border-neutral-200 bg-white p-10 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-display text-2xl font-semibold tracking-tight text-das-ink">
                Serving Gauteng & Botswana
              </h3>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-neutral-600">
                Online-first delivery, remote support and on-site implementation — for retail,
                e-commerce, tourism and small to medium businesses.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {["Johannesburg", "Gauteng", "South Africa", "Botswana"].map((m) => (
                <span
                  key={m}
                  className="rounded-full border border-das-violet/30 bg-das-violet/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-das-deep"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <CTABand
        title="Let's write your business's next chapter."
        copy="Book a consultation and we'll map the right mix of payments, POS and value-added services for where you are — and where you're going."
        testid="about-cta-band"
      />
    </div>
  );
}
